<template>
    <Transition
        :duration="SIDEBAR_DURATION"
        name="sidebar"
        appear
        @after-enter="onAfterEnter"
        @after-leave="onAnimationEnd"
    >
        <aside
            v-if="isShown"
            v-bind="$attrs"
            :id="id"
            class="sidebar"
            data-cy="sidebar"
            aria-label="sidebar"
        >
            <slot name="header">
                <PrimaryHeader
                    :title="title"
                    class="sidebar__header"
                >
                    <template #right-slot-desktop>
                        <slot
                            v-if="hasRightSlotDesktop"
                            name="right-slot-desktop"
                        />
                        <div
                            v-else
                            class="sidebar__header-actions"
                        >
                            <SidebarPinButton
                                v-if="pinnable"
                                v-model="pinned"
                            />
                            <Button
                                has-only-icon
                                variant="light"
                                size="sm"
                                aria-label="Close sidebar"
                                @click="hide"
                            >
                                <CloseIcon class="close-button__icon" />
                            </Button>
                        </div>
                    </template>
                </PrimaryHeader>
            </slot>

            <div
                class="sidebar__content"
                :class="contentClass"
            >
                <slot />
            </div>

            <div
                v-if="hasFooterSlot"
                :class="{
                    'sidebar-footer--padding': !noFooterPadding
                }"
            >
                <slot
                    name="footer"
                />
            </div>
        </aside>
    </Transition>
</template>

<script lang="ts" setup>
import {
    computed, onUnmounted, useSlots,
} from 'vue';
import ButtonKeyListener from '../../../scripts/helpers/listeners/ButtonKeyListener';
import type { SidebarProps } from './SidebarProps';
import PrimaryHeader from '../Header/PrimaryHeader.vue';
import CloseIcon from '../../../images/x.svg?component';
import Button from '../Elements/Button.vue';
import SidebarPinButton from './SidebarPinButton.vue';

const SIDEBAR_DURATION = 350;

type Props = {
    id: SidebarProps['id'],
    title?: SidebarProps['title'],
    isShown?: SidebarProps['isShown'],
    contentClass?: SidebarProps['contentClass'],
    pinnable?: SidebarProps['pinnable'],
    noFooterPadding?: boolean,
};

type Emits = {
    (event: 'hide'): void,
    (event: 'afterEnter'): void,
    (event: 'hidden'): void,
};

const props = withDefaults(defineProps<Props>(), {
    title: '',
    isShown: false,
    contentClass: null,
    pinnable: false,
    noFooterPadding: false,
});

const pinned = defineModel<boolean>('pinned', { default: false });

const slots = useSlots();

const emit = defineEmits<Emits>();
const hide = () => {
    emit('hide');
};

const hasRightSlotDesktop = computed<boolean>(() => !!slots['right-slot-desktop']);

const hasFooterSlot = computed<boolean>(() => !!slots.footer);

const onAfterEnter = () => {
    emit('afterEnter');
};

const buttonKeyListener = new ButtonKeyListener(
    (event: KeyboardEvent) => event.key === 'Escape' && props.isShown && !pinned.value,
    hide,
);

buttonKeyListener.registerListener();

onUnmounted(() => {
    buttonKeyListener.unregisterListener();
});

const onAnimationEnd = () => {
    emit('hidden');
};

</script>

<style lang="scss" scoped>
@import '../../../styles/abstracts/colors_old';
@import '../../../styles/abstracts/spacings';
@import '../../../styles/abstracts/functions';
@import '../../../styles/vendors/breakpoints';
@import '../../../styles/abstracts/mixins';
@import '../../../styles/abstracts/sizes';
@import '@/styles/abstracts/font-sizes';

.sidebar {
    display: flex;

    flex-shrink: 0;
    flex-direction: column;

    width: 21rem;
    max-width: 42rem;
    height: 100dvh;
    overflow: hidden;

    background-color: var(--theme-color-surface-primary-default);

    transition: width 0.35s ease;

    @include media-breakpoint-down(xl) {
        max-width: $admin-menu-width-13-inch;
    }

    @include media-breakpoint-down(lg) {
        max-width: $admin-menu-width-11-inch;
    }

    &__content {
        display: flex;

        flex-grow: 1;
        flex-direction: column;

        // Bound the flex child so inner scroll areas (e.g. the keywords list)
        // stay within the available space instead of overflowing under the footer.
        min-height: 0;

        overflow: hidden;
    }

    &__header-actions {
        display: flex;

        gap: $spacing-xs;

        align-items: center;
    }
}

.sidebar-enter-active,
.sidebar-leave-active {
    white-space: nowrap;

}

.sidebar-enter-from,
.sidebar-leave-to {
    width: 0;
}

.sidebar-footer--padding {
    padding: $spacing-s $spacing-xl;

    @include media-breakpoint-down(lg) {
        padding: $spacing-s;
    }
}

.close-button__icon {
    width: $icon-size-lg !important;
    height: $icon-size-lg !important;
}
</style>
